<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import TrustTabs from './trust-tabs.vue';
import CompetitorsTab from './competitors-tab.vue';
import HandsOnProcessTab from './hands-on-process-tab.vue';
import TrackAnytimeTab from './track-anytime-tab.vue';
import styles from './trust-experience-block-style.scss';

const b = bem('trust-experience-block', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'TrustExperienceBlock',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    TitleWithLinesBlock,
    TrustTabs,
    CompetitorsTab,
    HandsOnProcessTab,
    TrackAnytimeTab,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      selectedTab: null,
      // Modal visibility
      titleModalVisible: false,
      descriptionModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    isMobile() {
      return this.isMobileView;
    },
    titleList() {
      return this.data?.titleList || [];
    },
    tabs() {
      return this.data?.tabs || [];
    },
    hasTitleList() {
      return this.titleList.length > 0;
    },
    description() {
      return this.data?.description || '';
    },
    descriptionMobile() {
      return this.data?.descriptionMobile || this.description;
    },
    hasDescription() {
      return !!this.description;
    },
  },
  mounted() {
    this.initializeTabs();
  },
  watch: {
    data: {
      handler() {
        this.initializeTabs();
      },
      immediate: true,
    },
  },
  methods: {
    // Tab methods
    changeTab(tabId) {
      const tab = this.tabs.find((t) => t.id === tabId);
      if (tab) {
        this.selectedTab = tab;
      }
    },
    initializeTabs() {
      if (this.tabs.length > 0) {
        if (this.selectedTab) {
          // Обновляем selectedTab на новую ссылку с тем же id
          const updated = this.tabs.find((t) => t.id === this.selectedTab.id);
          this.selectedTab = updated || this.tabs[0];
        } else {
          [this.selectedTab] = this.tabs;
        }
      }
    },
    // Modal open methods
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    // Save handlers
    handleTitleSave(values) {
      this.$emit('update', { titleList: values });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', { titleList: [] });
      this.titleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', {
        description: '',
        descriptionMobile: '',
      });
      this.descriptionModalVisible = false;
    },
    // Tab content update handler (from child components)
    handleTabUpdate(tabId, updates) {
      const newTabs = this.tabs.map((tab) => {
        if (tab.id === tabId) {
          return { ...tab, ...updates };
        }
        return tab;
      });
      this.$emit('update', { tabs: newTabs });
    },
    // Tab title update handler (from TrustTabs)
    handleTabTitleUpdate({ index, updates }) {
      const newTabs = [...this.tabs];
      if (newTabs[index]) {
        newTabs[index] = { ...newTabs[index], ...updates };
        this.$emit('update', { tabs: newTabs });
      }
    },
  },
};
</script>

<template>
  <article :class="b('', { mix: className, isMobileView, isEditMode })">
    <!-- Title -->
    <div
      v-if="hasTitleList || isEditMode"
      :class="b('title-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openTitleModal"
      @keyup.enter="openTitleModal"
      @keyup.space="openTitleModal"
    >
      <TitleWithLinesBlock
        v-if="hasTitleList"
        :class="b('title', { isMobileView })"
        :is-mobile-view="isMobileView"
        :text-list="titleList"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add title
      </div>
      <PencilIcon
        v-if="isEditMode && hasTitleList"
        :class="b('edit-icon')"
        color="#293241"
        :size="20"
      />
    </div>

    <!-- Description -->
    <div
      v-if="hasDescription || isEditMode"
      :class="b('description-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openDescriptionModal"
      @keyup.enter="openDescriptionModal"
      @keyup.space="openDescriptionModal"
    >
      <p v-if="hasDescription" :class="b('description', { isMobileView })">
        {{ isMobileView ? descriptionMobile : description }}
      </p>
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add description
      </div>
      <PencilIcon
        v-if="isEditMode && hasDescription"
        :class="b('edit-icon')"
        color="#5e5d5d"
        :size="16"
      />
    </div>

    <!-- Tabs -->
    <TrustTabs
      v-if="tabs && tabs.length > 1"
      :class="b('tabs', { isMobileView })"
      :tabs="tabs"
      :is-mobile-view="isMobileView"
      :is-left="false"
      :is-right="false"
      :is-edit-mode="isEditMode"
      @tab-change="changeTab"
      @update-tab="handleTabTitleUpdate"
    >
      <!-- Слоты для контента табов в мобильном виде -->
      <template v-for="tab in tabs" :key="tab.id" #[`tab-${tab.id}`]>
        <CompetitorsTab
          v-if="tab.id === 'competitors'"
          :items="tab.items"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
        <HandsOnProcessTab
          v-else-if="tab.id === 'process'"
          :items="tab.items"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
        <TrackAnytimeTab
          v-else-if="tab.id === 'products' || tab.id === 'support'"
          :title="tab.title"
          :title-mob="tab.titleMob"
          :descriptions="tab.descriptions"
          :image-src="tab.imageSrc"
          :is-left="tab.isLeft"
          :link-url="tab.linkUrl"
          :link-text="tab.linkText"
          :with-link="tab.withLink"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
      </template>

      <!-- Слоты для контента в десктопном виде -->
      <template v-for="tab in tabs" :key="`content-${tab.id}`" #[`content-${tab.id}`]>
        <CompetitorsTab
          v-if="tab.id === 'competitors'"
          :items="tab.items"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
        <HandsOnProcessTab
          v-else-if="tab.id === 'process'"
          :items="tab.items"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
        <TrackAnytimeTab
          v-else-if="tab.id === 'products' || tab.id === 'support'"
          :title="tab.title"
          :title-mob="tab.titleMob"
          :descriptions="tab.descriptions"
          :image-src="tab.imageSrc"
          :is-left="tab.isLeft"
          :link-url="tab.linkUrl"
          :link-text="tab.linkText"
          :with-link="tab.withLink"
          :is-mobile-view="isMobileView"
          :is-edit-mode="isEditMode"
          :isPublished="isPublished"
          @update="(updates) => handleTabUpdate(tab.id, updates)"
        />
      </template>
    </TrustTabs>

    <!-- Modals -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="titleList"
      :show-delete="hasTitleList"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      title="Edit Block Description"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />
  </article>
</template>
